import { useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import {
  CreateUserInteractionEventMutation,
  CreateUserInteractionEventMutationVariables,
  GetAutoCheckEnableForApplicantsContextQuery,
  GetAutoCheckEnableForApplicantsContextQueryVariables,
  GetAutoCheckEnablementContextQuery,
  GetAutoCheckEnablementContextQueryVariables,
} from 'types/graphql'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

const GetAutoCheckEnablementContext = gql`
  query GetAutoCheckEnablementContextQuery($input: GetAutoCheckEnablementContextInput!) {
    getAutoCheckEnablementContext(input: $input) {
      alerts {
        recordTypeId
        recordTypeName
        addressedAutoCheckCount
      }
      isEligibleForInContextPrompt
    }
  }
`

export const useGetAutoCheckEnablementContextQuery = ({ enabled }: { enabled: boolean }) => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<
    GetAutoCheckEnablementContextQuery,
    GetAutoCheckEnablementContextQueryVariables
  >({
    queryDocument: GetAutoCheckEnablementContext,
    queryKey: [QueryKey.AutoCheckEnablementContext, organizationId],
    variables: { input: { organizationId: organizationId ?? -1 } },
    enabled: enabled && !!organizationId,
  })
  const alerts = useMemo(() => data?.getAutoCheckEnablementContext.alerts ?? [], [data])
  const isEligibleForInContextPrompt =
    data?.getAutoCheckEnablementContext.isEligibleForInContextPrompt ?? false
  return {
    ...rest,
    alerts,
    isEligibleForInContextPrompt,
  }
}

const GetAutoCheckEnableForApplicantsContext = gql`
  query GetAutoCheckEnableForApplicantsContextQuery(
    $input: GetAutoCheckEnableForApplicantsContextInput!
  ) {
    getAutoCheckEnableForApplicantsContext(input: $input) {
      totalEstimatedHoursSaved
    }
  }
`

export const useGetAutoCheckEnableForApplicantsContextQuery = () => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<
    GetAutoCheckEnableForApplicantsContextQuery,
    GetAutoCheckEnableForApplicantsContextQueryVariables
  >({
    queryDocument: GetAutoCheckEnableForApplicantsContext,
    queryKey: [QueryKey.AutoCheckEnableForApplicantsContext, organizationId],
    variables: { input: { organizationId: organizationId ?? -1 } },
    enabled: !!organizationId,
  })
  const totalEstimatedHoursSaved =
    data?.getAutoCheckEnableForApplicantsContext.totalEstimatedHoursSaved ?? 0
  return {
    ...rest,
    totalEstimatedHoursSaved,
  }
}

const CreateUserInteractionEvent = gql`
  mutation CreateUserInteractionEventMutation($input: CreateUserInteractionEventInput!) {
    createUserInteractionEvent(input: $input) {
      id
    }
  }
`

export const useCreateUserInteractionEventMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<
    CreateUserInteractionEventMutation,
    CreateUserInteractionEventMutationVariables
  >({
    mutationDocument: CreateUserInteractionEvent,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.AutoCheckEnablementContext],
      })
    },
  })
}
